/**
 * RunsView — Creative Review runs dashboard.
 *
 * Richer data source: combines strategic_rebuild garden runs with the
 * committed briefs they produced. KPI tiles + recharts visualizations
 * (status mix, runs/day, briefs/day) + a run list with drill-down.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, ChevronRight, ExternalLink, Play, X, Search, CheckSquare, Square, ImageOff } from 'lucide-react';
import { clsx } from 'clsx';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import {
  gardenApi,
  generationApi,
  adCatalogApi,
  type GardenRun,
  type GardenRunType,
  type GenerationBrief,
  type GardenRunCreateRequest,
  type AdCatalogCreativeSummary,
} from '@/services/api';
import { subscribeToGardenRuns } from '@/services/firestoreListeners';
import { RunStatusBadge } from '@/components/pdo/StatusBadges';
import { EmptyState } from '@/components/pdo/EmptyState';
import { ErrorBanner } from '@/components/pdo/ErrorBanner';
import { useFetch, timeAgo, formatNumber } from '@/components/pdo/utils';

// All run types surfaced in the Creative Review section.
const CREATIVE_REVIEW_RUN_TYPES: GardenRunType[] = [
  'strategic_rebuild',
  'creative_review_backfill',
  'creative_review_refresh',
  'creative_review_seed',
];

const RUNNING = new Set(['running', 'pending', 'queued']);
const DAYS = 14;

const STATUS_COLORS: Record<string, string> = {
  completed: '#16a34a',
  failed: '#dc2626',
  running: '#0ea5e9',
  pending: '#f59e0b',
  queued: '#f59e0b',
  pending_review: '#8b5cf6',
};

/** A brief produced by a committed strategic_rebuild run carries a
 *  strategic_cluster_id (or the legacy metadata key). */
function isStrategicRebuildBrief(b: GenerationBrief): boolean {
  return (
    !!b.strategic_cluster_id ||
    !!(b.metadata && b.metadata['strategic_rebuild_cluster_id'])
  );
}

function dayKey(iso?: string | null): string | null {
  if (!iso) return null;
  return iso.slice(0, 10);
}

function lastNDays(n: number): string[] {
  const out: string[] = [];
  const d = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const x = new Date(d);
    x.setUTCDate(d.getUTCDate() - i);
    out.push(x.toISOString().slice(0, 10));
  }
  return out;
}

function Kpi({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div
        className={clsx('mt-1 text-2xl font-semibold', tone ?? 'text-slate-900')}
      >
        {value}
      </div>
    </div>
  );
}

function ChartCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <h3 className="text-xs font-semibold text-slate-600 mb-3">{title}</h3>
      <div style={{ width: '100%', height: 200 }}>{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// StartRunModal
// ---------------------------------------------------------------------------

type RunTypeOption = {
  value: GardenRunType;
  label: string;
  description: string;
};
const RUN_TYPE_OPTIONS: RunTypeOption[] = [
  {
    value: 'creative_review_backfill',
    label: 'Creative Review — Backfill',
    description:
      'Phase 1: Review top-spend deployed creatives against active personas, emit Knowledge Concepts.',
  },
  {
    value: 'creative_review_refresh',
    label: 'Creative Review — Refresh',
    description:
      'Phase 2 (post graph-build): Map raw KCs → resolved concepts, recompute persona fit scores.',
  },
  {
    value: 'creative_review_seed',
    label: 'Seed (personas / taxonomy / products)',
    description:
      'Sync personas to BigQuery, then seed taxonomy, persona research, and products (Cloud Tasks when not local).',
  },
];

// ---------------------------------------------------------------------------
// CreativePicker — inline search + multi-select for specific creative IDs
// ---------------------------------------------------------------------------

function CreativePicker({
  clientUid,
  selectedIds,
  onToggle,
}: {
  clientUid: string;
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AdCatalogCreativeSummary[]>([]);
  const [fetching, setFetching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const doSearch = useCallback(
    async (q: string) => {
      setFetching(true);
      try {
        const resp = await adCatalogApi.listCreatives({
          client_uid: clientUid,
          q: q || undefined,
          sort: 'spend',
          limit: 30,
        });
        setResults(resp.items);
      } catch {
        // ignore
      } finally {
        setFetching(false);
      }
    },
    [clientUid],
  );

  // Load initial results on mount
  useEffect(() => {
    doSearch('');
  }, [doSearch]);

  const handleQueryChange = (val: string) => {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const formatSpend = (n?: number | null) =>
    n != null ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : null;

  return (
    <div className="flex flex-col gap-2">
      {/* Search box */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          placeholder="Search by name, headline, copy…"
          className="w-full rounded-lg border border-slate-200 pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal/40"
        />
      </div>

      {/* Selected count + no-image warning */}
      {selectedIds.size > 0 && (() => {
        const noImageCount = results.filter(
          (c) => selectedIds.has(c.creative_id) && c.has_image === false,
        ).length;
        return (
          <div className="flex items-center gap-2">
            <p className="text-xs font-medium text-brand-teal">
              {selectedIds.size} creative{selectedIds.size !== 1 ? 's' : ''} selected
            </p>
            {noImageCount > 0 && (
              <p className="text-xs text-amber-600">
                ⚠ {noImageCount} will score copy-only (no image)
              </p>
            )}
          </div>
        );
      })()}

      {/* Results list */}
      <div className="max-h-52 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
        {fetching && results.length === 0 && (
          <div className="px-3 py-4 text-xs text-slate-400 text-center">Loading…</div>
        )}
        {!fetching && results.length === 0 && (
          <div className="px-3 py-4 text-xs text-slate-400 text-center">No creatives found</div>
        )}
        {results.map((c) => {
          const checked = selectedIds.has(c.creative_id);
          const spend = formatSpend(c.performance?.spend);
          const hasImage = c.has_image !== false; // treat null/undefined as unknown (optimistic)
          const label =
            c.form?.headline ||
            c.ad_name ||
            c.creative_id;
          const sub = [c.form?.media_type, spend].filter(Boolean).join(' · ');
          return (
            <button
              key={c.creative_id}
              type="button"
              onClick={() => onToggle(c.creative_id)}
              className={clsx(
                'w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors',
                checked ? 'bg-brand-teal/5' : 'hover:bg-slate-50',
              )}
            >
              {/* Thumbnail */}
              {c.form?.thumbnail_url ? (
                <img
                  src={c.form.thumbnail_url}
                  alt=""
                  className="w-8 h-8 rounded object-cover flex-shrink-0 bg-slate-100"
                />
              ) : (
                <div
                  className="w-8 h-8 rounded flex-shrink-0 flex items-center justify-center bg-slate-100"
                  title={!hasImage ? 'No image found — review will score copy only' : 'Loading…'}
                >
                  {!hasImage && (
                    <ImageOff className="w-3.5 h-3.5 text-slate-400" />
                  )}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-800 truncate">{label}</p>
                <p className="text-xs text-slate-400 truncate">
                  {sub}
                  {!hasImage && (
                    <span className="ml-1.5 text-amber-500 font-medium">· no image</span>
                  )}
                </p>
              </div>
              {checked ? (
                <CheckSquare className="w-4 h-4 text-brand-teal flex-shrink-0" />
              ) : (
                <Square className="w-4 h-4 text-slate-300 flex-shrink-0" />
              )}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-slate-400">
        Showing top 30 by spend. Use search to find others.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// StartRunModal
// ---------------------------------------------------------------------------

function StartRunModal({
  clientUid,
  onClose,
  onStarted,
}: {
  clientUid: string;
  onClose: () => void;
  onStarted: (runId: string) => void;
}) {
  const [runType, setRunType] = useState<GardenRunType>(
    'creative_review_backfill',
  );
  const [top, setTop] = useState<string>('5');
  // Selection mode: auto (top-N spend ranked) vs manual (pick specific IDs).
  const [selectionMode, setSelectionMode] = useState<'auto' | 'manual'>('auto');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Review depth: FAST (observation-only, default) vs FULL (with synthesis).
  const [reviewDepth, setReviewDepth] = useState<'fast' | 'full'>('fast');
  // Initiation: bootstrap (initial onboarding) vs recurring (new creatives).
  const [initiation, setInitiation] = useState<'bootstrap' | 'recurring'>(
    'bootstrap',
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Reset selection when run type changes away from backfill
  useEffect(() => {
    if (runType !== 'creative_review_backfill') {
      setSelectionMode('auto');
      setSelectedIds(new Set());
    }
  }, [runType]);

  const toggleCreative = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Close on backdrop click
  const onBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === dialogRef.current) onClose();
  };

  const handleStart = async () => {
    setLoading(true);
    setError(null);
    try {
      const topN = parseInt(top, 10);

      const seedOverrides =
        runType === 'creative_review_seed'
          ? {
              creative_review_seed: {
                // env/mode inferred server-side from CORE_API_BASE_URL unless set here
                execute: true,
              },
            }
          : undefined;

      let backfillOverrides: Record<string, unknown> | undefined;
      if (runType === 'creative_review_backfill') {
        if (selectionMode === 'manual' && selectedIds.size > 0) {
          backfillOverrides = {
            creative_review_backfill: {
              creative_subset_ids: Array.from(selectedIds),
            },
          };
        } else if (!isNaN(topN) && topN > 0) {
          backfillOverrides = { creative_review_backfill: { top: topN } };
        }
      }

      // Depth/initiation apply to the review passes (backfill + strategic
      // rebuild), not to the seed run.
      const isReviewPass =
        runType === 'creative_review_backfill' ||
        runType === 'creative_review_refresh' ||
        runType === 'strategic_rebuild';
      const req: GardenRunCreateRequest = {
        run_type: runType,
        trigger_source: 'manual',
        stage_config_overrides: seedOverrides ?? backfillOverrides,
        ...(isReviewPass
          ? { review_depth: reviewDepth, initiation }
          : {}),
      };
      const resp = await gardenApi.triggerRun(clientUid, req);
      onStarted(resp.run_id);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const selectedOption = RUN_TYPE_OPTIONS.find((o) => o.value === runType);

  const startDisabled =
    loading ||
    (runType === 'creative_review_backfill' &&
      selectionMode === 'manual' &&
      selectedIds.size === 0);

  return (
    <div
      ref={dialogRef}
      onClick={onBackdrop}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    >
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-700"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-base font-semibold text-slate-900 mb-4">
          Start a new review run
        </h2>

        <label className="block text-xs font-medium text-slate-600 mb-1">
          Run type
        </label>
        <select
          value={runType}
          onChange={(e) => setRunType(e.target.value as GardenRunType)}
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-teal/40 mb-1"
        >
          {RUN_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {selectedOption && (
          <p className="text-xs text-slate-500 mb-4">
            {selectedOption.description}
          </p>
        )}

        {runType === 'creative_review_backfill' && (
          <div className="mb-4">
            {/* Selection mode toggle */}
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Creative selection
            </label>
            <div className="flex rounded-lg border border-slate-200 overflow-hidden mb-3">
              <button
                type="button"
                onClick={() => setSelectionMode('auto')}
                className={clsx(
                  'flex-1 px-3 py-1.5 text-xs font-medium transition-colors',
                  selectionMode === 'auto'
                    ? 'bg-brand-teal text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50',
                )}
              >
                Auto — top spend
              </button>
              <button
                type="button"
                onClick={() => setSelectionMode('manual')}
                className={clsx(
                  'flex-1 px-3 py-1.5 text-xs font-medium transition-colors border-l border-slate-200',
                  selectionMode === 'manual'
                    ? 'bg-brand-teal text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50',
                )}
              >
                Pick specific
              </button>
            </div>

            {selectionMode === 'auto' ? (
              <>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Max creatives to review
                </label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={top}
                  onChange={(e) => setTop(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal/40"
                  placeholder="e.g. 20"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Selects the top-N deployed, spend-ranked creatives.
                </p>
              </>
            ) : (
              <CreativePicker
                clientUid={clientUid}
                selectedIds={selectedIds}
                onToggle={toggleCreative}
              />
            )}
          </div>
        )}

        {(runType === 'creative_review_backfill' ||
          runType === 'creative_review_refresh' ||
          runType === 'strategic_rebuild') && (
          <div className="mb-4 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Review depth
              </label>
              <select
                value={reviewDepth}
                onChange={(e) =>
                  setReviewDepth(e.target.value as 'fast' | 'full')
                }
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-teal/40"
              >
                <option value="fast">Fast — observation only</option>
                <option value="full">Full — with recommendations</option>
              </select>
              <p className="text-xs text-slate-400 mt-1">
                {reviewDepth === 'fast'
                  ? 'Extracts concepts + flags watch-items. No verdict, scores, or recommendations.'
                  : 'Full analysis incl. audience quality + recommendation synthesis.'}
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Initiation
              </label>
              <select
                value={initiation}
                onChange={(e) =>
                  setInitiation(e.target.value as 'bootstrap' | 'recurring')
                }
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-teal/40"
              >
                <option value="bootstrap">Bootstrap — onboard account</option>
                <option value="recurring">Recurring — new creatives</option>
              </select>
              <p className="text-xs text-slate-400 mt-1">
                {initiation === 'bootstrap'
                  ? 'Initial pass over all deployed, spending creatives.'
                  : 'Incremental pass over creatives not yet reviewed.'}
              </p>
            </div>
          </div>
        )}

        {error && (
          <p className="text-xs text-red-600 mb-3">
            Error starting run: {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900"
          >
            Cancel
          </button>
          <button
            onClick={handleStart}
            disabled={startDisabled}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-brand-teal text-white text-sm font-medium hover:bg-brand-teal/90 disabled:opacity-50"
            title={
              startDisabled && selectionMode === 'manual'
                ? 'Select at least one creative'
                : undefined
            }
          >
            <Play className="w-3.5 h-3.5" />
            {loading
              ? 'Starting…'
              : selectionMode === 'manual' && selectedIds.size > 0
                ? `Start run (${selectedIds.size})`
                : 'Start run'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// RunsView
// ---------------------------------------------------------------------------

export function RunsView({ clientUid }: { clientUid: string }) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string | null>(null);
  const [showStartModal, setShowStartModal] = useState(false);

  // Real-time Firestore subscription — no polling needed.
  const [allRuns, setAllRuns] = useState<GardenRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [runsError, setRunsError] = useState<string | null>(null);

  useEffect(() => {
    setRunsLoading(true);
    setRunsError(null);
    const unsub = subscribeToGardenRuns(
      clientUid,
      (runs) => {
        setAllRuns(runs);
        setRunsLoading(false);
      },
      (err) => {
        setRunsError(err.message);
        setRunsLoading(false);
      },
    );
    return unsub;
  }, [clientUid]);

  const briefsQuery = useFetch(
    () => generationApi.listBriefs({ client_uid: clientUid, limit: 100 }),
    [clientUid],
  );

  const runs: GardenRun[] = useMemo(
    () => allRuns.filter((r) => CREATIVE_REVIEW_RUN_TYPES.includes(r.run_type)),
    [allRuns],
  );
  const srBriefs: GenerationBrief[] = useMemo(
    () =>
      (briefsQuery.data?.briefs ?? []).filter(
        (b) => isStrategicRebuildBrief(b) && b.client_uid === clientUid,
      ),
    [briefsQuery.data, clientUid],
  );

  const detailQuery = useFetch(
    () =>
      selected
        ? gardenApi.getRun(clientUid, selected)
        : Promise.resolve(null as unknown as GardenRun),
    [clientUid, selected],
  );

  const total = runs.length;
  const completed = runs.filter((r) => r.status === 'completed').length;
  const failed = runs.filter((r) => r.status === 'failed').length;
  const successRate = total ? Math.round((completed / total) * 100) : 0;
  const last = runs[0];
  const avgBriefs =
    completed > 0 ? (srBriefs.length / completed).toFixed(1) : '—';

  const statusData = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const r of runs) counts[r.status] = (counts[r.status] ?? 0) + 1;
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [runs]);

  const timeline = useMemo(() => {
    const days = lastNDays(DAYS);
    const runByDay: Record<string, number> = {};
    const briefByDay: Record<string, number> = {};
    for (const r of runs) {
      const k = dayKey(r.started_at);
      if (k) runByDay[k] = (runByDay[k] ?? 0) + 1;
    }
    for (const b of srBriefs) {
      const k = dayKey(b.created_at);
      if (k) briefByDay[k] = (briefByDay[k] ?? 0) + 1;
    }
    return days.map((d) => ({
      day: d.slice(5),
      runs: runByDay[d] ?? 0,
      briefs: briefByDay[d] ?? 0,
    }));
  }, [runs, srBriefs]);

  return (
    <div className="space-y-6">
      {showStartModal && (
        <StartRunModal
          clientUid={clientUid}
          onClose={() => setShowStartModal(false)}
          onStarted={(runId) => {
            navigate(`/admin/creative-review/${clientUid}/runs/${runId}`);
          }}
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Total runs" value={formatNumber(total)} />
        <Kpi
          label="Completed"
          value={formatNumber(completed)}
          tone="text-success"
        />
        <Kpi
          label="Failed"
          value={formatNumber(failed)}
          tone={failed ? 'text-danger' : 'text-slate-900'}
        />
        <Kpi label="Success rate" value={`${successRate}%`} />
        <Kpi
          label="Briefs / run"
          value={String(avgBriefs)}
          tone="text-brand-teal"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Run status mix">
          {statusData.length === 0 ? (
            <EmptyState title="No runs" compact />
          ) : (
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={statusData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={45}
                  outerRadius={75}
                >
                  {statusData.map((s) => (
                    <Cell
                      key={s.name}
                      fill={STATUS_COLORS[s.name] ?? '#94a3b8'}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title={`Runs / day (last ${DAYS}d)`}>
          <ResponsiveContainer>
            <BarChart data={timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={1} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={24} />
              <Tooltip />
              <Bar dataKey="runs" fill="#0ea5e9" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={`Committed briefs / day (last ${DAYS}d)`}>
          <ResponsiveContainer>
            <BarChart data={timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={1} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={24} />
              <Tooltip />
              <Bar dataKey="briefs" fill="#14b8a6" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">
          Recent runs
          {last?.started_at && (
            <span className="ml-2 font-normal text-slate-400">
              last {timeAgo(last.started_at)}
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => briefsQuery.refetch()}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800"
          >
            <RefreshCw
              className={clsx(
                'w-3.5 h-3.5',
                runsLoading && 'animate-spin',
              )}
            />
            Refresh
          </button>
          <button
            onClick={() => setShowStartModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-teal text-white text-xs font-medium hover:bg-brand-teal/90"
          >
            <Play className="w-3.5 h-3.5" />
            Start run
          </button>
        </div>
      </div>

      {runsError && (
        <ErrorBanner error={runsError} onRetry={() => { setRunsError(null); setRunsLoading(true); }} />
      )}

      {!runsLoading && runs.length === 0 && !runsError && (
        <EmptyState
          title="No creative review runs yet"
          hint={
            srBriefs.length > 0
              ? `${srBriefs.length} brief${srBriefs.length !== 1 ? 's' : ''} found for this client but no run records are stored yet. Runs will appear here after the Creative Review agent executes.`
              : 'Runs will appear here once the Creative Review agent has executed for this client.'
          }
        />
      )}

      <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
        {runs.map((r) => {
          const isOpen = selected === r.run_id;
          return (
            <div key={r.run_id}>
              <button
                onClick={() =>
                  navigate(`/admin/creative-review/${clientUid}/runs/${r.run_id}`)
                }
                onDoubleClick={() => setSelected(isOpen ? null : r.run_id)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 group"
              >
                <ChevronRight
                  className={clsx(
                    'w-4 h-4 text-slate-400 transition-transform shrink-0',
                    isOpen && 'rotate-90',
                  )}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-slate-600">
                      {r.run_id}
                    </span>
                    <RunStatusBadge status={r.status} />
                    <span className="text-xs text-slate-400 capitalize">
                      {r.run_type?.replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>
                <span className="text-xs text-slate-400 shrink-0">
                  {r.started_at ? timeAgo(r.started_at) : '—'}
                </span>
                <span className="flex items-center gap-1 text-xs text-brand-teal opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <ExternalLink className="w-3.5 h-3.5" />
                  View creatives
                </span>
              </button>
              {isOpen && (
                <div className="px-6 pb-4 pt-1 bg-slate-50/60">
                  {detailQuery.loading && (
                    <p className="text-xs text-slate-500">Loading detail…</p>
                  )}
                  {detailQuery.error && (
                    <p className="text-xs text-danger">{detailQuery.error}</p>
                  )}
                  {detailQuery.data && (
                    <StageOutcomes run={detailQuery.data} />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StageOutcomes({ run }: { run: GardenRun }) {
  const outcomes = Object.entries(run.stage_outcomes ?? {});
  if (outcomes.length === 0) {
    return (
      <p className="text-xs text-slate-500">
        No stage outcomes recorded
        {run.error ? ` · error: ${run.error}` : ''}.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      {outcomes.map(([stage, payload]) => {
        const p = payload as Record<string, unknown>;
        return (
          <div
            key={stage}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-700">
                {stage}
              </span>
              <span className="text-xs text-slate-500">
                {String(p.status ?? '—')}
              </span>
            </div>
            {p.summary != null && (
              <pre className="mt-1 text-[11px] text-slate-500 overflow-x-auto">
                {JSON.stringify(p.summary, null, 2)}
              </pre>
            )}
          </div>
        );
      })}
    </div>
  );
}
